import { RouteProp, useRoute } from '@react-navigation/native';
import { LayoutContext, ScreenSize } from 'contexts/LayoutContext';
import { Pressable, useDripsyTheme, View } from 'dripsy';
import _ from 'lodash';
import React, { useContext, useEffect } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { useBillPaymentForm } from '../../hooks/useBillPaymentForm.hook';
import { useCreateBillPayment } from '../../hooks/useCreateBillPayment.hook';
import { BillPaymentStackParamList, BillPaymentStackRoutes } from '../../payment.navigator';
import { isAmountPositive, openImage64, openURL } from '../../utils/utils';
import { AttachmentViewer } from '../components/attachmentViewer/AttachmentViewer';
import { BillPaymentFormMobileComponent } from './BillPaymentFormMobile.component';
import { BillPaymentFormWebComponent } from './BillPaymentFormWeb.component';

export const BillPaymentFormScreen: React.FC = () => {
  const layout = useContext(LayoutContext);
  const route = useRoute<RouteProp<BillPaymentStackParamList, BillPaymentStackRoutes.Form>>();
  const { request, response: responseProp, documentSelected, paymentType } = route.params;
  const billPaymentForm = useBillPaymentForm();
  const createBillPayment = useCreateBillPayment();

  const { theme } = useDripsyTheme();
  const { $3 } = theme.space || ({} as any);
  const { width } = useWindowDimensions();
  let widthScaleImage = 0;
  let heightScaleImage = 0;
  if (layout.screenSize === ScreenSize.Tablet || layout.isMobile) {
    widthScaleImage = width - ($3 as number) * 2;
    heightScaleImage = (widthScaleImage / 343) * 250;
  } else {
    widthScaleImage = (width / 154) * 44;
    heightScaleImage = (widthScaleImage / 44) * 34;
  }

  const {
    detailForm: { setBankName, setBankAccountNumber, setAssetName, setAmount, setWalletId, setTransferPeerId, setInstructionId },
    scheduleForm: { setRemarks },
    general: { setQuoteDataRequestProps, setDocumentSelected, setQuoteResponseProps }
  } = billPaymentForm;

  useEffect(() => {
    if (!_.isUndefined(request) && !_.isUndefined(responseProp)) {
      const bankName = responseProp.attributes?.destination?.attributes?.recipient;
      const amount = isAmountPositive(responseProp.attributes?.amount) ? responseProp.attributes?.amount?.toString() : '0';
      setBankName(bankName);
      setBankAccountNumber(responseProp.attributes?.destination?.attributes?.accountNumber);
      setAssetName(request.attributes.asset ?? 'HKD');
      setAmount(amount);
      setWalletId(request.attributes.source?.attributes?.walletId ?? '');
      setRemarks(responseProp.remarks ?? '');
      setTransferPeerId(responseProp.attributes?.destination?.attributes?.transferPeerId);
      setInstructionId(responseProp?.id ?? '');
      setDocumentSelected(documentSelected);
    }
    setQuoteResponseProps(responseProp);
    setQuoteDataRequestProps(request);
    createBillPayment.general.setPaymentType(paymentType);
  }, [request, responseProp]);

  const renderImage = (): JSX.Element => {
    if (_.isNumber(widthScaleImage) && _.isNumber(heightScaleImage)) {
      if (!_.isEmpty(createBillPayment.general.getThumbnailUrLFromResponse(responseProp))) {
        return (
          <Pressable onPress={async () => openURL(createBillPayment.general.getThumbnailUrLFromResponse(responseProp))}>
            <AttachmentViewer
              width={widthScaleImage}
              padding={Platform.OS === 'web' ? 10 : 0}
              height={heightScaleImage}
              imageUrl={createBillPayment.general.getThumbnailUrLFromResponse(responseProp)}
            />
          </Pressable>
        );
      }
      if (
        documentSelected &&
        documentSelected.type === 'success' &&
        (documentSelected.mimeType == 'image/png' || documentSelected.mimeType == 'image/jpeg')
      ) {
        return (
          <Pressable onPress={() => openImage64(documentSelected.uri)}>
            <AttachmentViewer width={widthScaleImage} padding={10} height={heightScaleImage} imageUrl={documentSelected.uri} />
          </Pressable>
        );
      }
    }
    return <></>;
  };

  if (layout.screenSize === ScreenSize.Tablet || layout.isMobile) {
    return (
      <View sx={{ flex: 1 }}>
        <BillPaymentFormMobileComponent
          useBillPaymentForm={billPaymentForm}
          useCreateBillPayment={createBillPayment}
          imageComponent={renderImage()}
        />
      </View>
    );
  }
  return (
    <BillPaymentFormWebComponent
      useBillPaymentForm={billPaymentForm}
      useCreateBillPayment={createBillPayment}
      imageComponent={renderImage()}
    />
  );
};
